/**
 * Confronto territoriale: primary territory vs up to 4 comparison territories
 */
import { useMemo } from "react";
import type { IstatPresenzeMensili } from "../../types/database";

const MONTH_LABELS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const COLORS = ["#284899", "#e07b39", "#8b5cf6", "#06b6d4", "#f43f5e"];

interface ComparisonChartProps {
  primarySeries: IstatPresenzeMensili[];
  primaryLabel: string;
  compareSeries: IstatPresenzeMensili[][];
  compareLabels: string[];
}

function toMonthly(data: IstatPresenzeMensili[]): number[] {
  return Array.from({ length: 12 }, (_, i) => {
    const found = data.find(p => p.mese === i + 1);
    return found?.presenze || 0;
  });
}

function formatNum(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(0) + "K";
  return String(n);
}

export function ComparisonChart({ primarySeries, primaryLabel, compareSeries, compareLabels }: ComparisonChartProps) {
  const allSeries = useMemo(() => {
    const primary = toMonthly(primarySeries);
    const others = compareSeries.map(s => toMonthly(s));
    return [{ label: primaryLabel, monthly: primary }, ...others.map((m, i) => ({ label: compareLabels[i], monthly: m }))];
  }, [primarySeries, primaryLabel, compareSeries, compareLabels]);

  const max = useMemo(() => {
    let m = 1;
    for (const s of allSeries) {
      for (const v of s.monthly) if (v > m) m = v;
    }
    return m;
  }, [allSeries]);

  if (allSeries.length < 2) return null;

  const seriesCount = allSeries.length;
  const svgW = 700;
  const svgH = 300;
  const padL = 60;
  const padR = 20;
  const padT = 20;
  const padB = 40;
  const plotW = svgW - padL - padR;
  const plotH = svgH - padT - padB;
  const groupW = plotW / 12;
  const barW = (groupW * 0.7) / seriesCount;

  const scaleY = (v: number) => padT + plotH - (v / max) * plotH;
  const scaleX = (monthIdx: number) => padL + (monthIdx + 0.5) * groupW;

  // Tooltip data
  const totals = allSeries.map(s => s.monthly.reduce((a, b) => a + b, 0));

  return (
    <div>
      <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full">
        {/* Grid */}
        {[0, 0.25, 0.5, 0.75, 1].map(frac => {
          const y = padT + plotH * (1 - frac);
          return (
            <g key={frac}>
              <line x1={padL} y1={y} x2={svgW - padR} y2={y} stroke="#e5e7eb" strokeWidth={0.5} />
              <text x={padL - 6} y={y + 4} textAnchor="end" fontSize={10} fill="#9ca3af">
                {formatNum(max * frac)}
              </text>
            </g>
          );
        })}

        {/* Bars */}
        {Array.from({ length: 12 }, (_, monthIdx) => {
          const cx = scaleX(monthIdx);
          const totalW = barW * seriesCount;
          const startX = cx - totalW / 2;
          return (
            <g key={monthIdx}>
              {allSeries.map((s, sIdx) => {
                const val = s.monthly[monthIdx];
                const h = (val / max) * plotH;
                return (
                  <rect
                    key={sIdx}
                    x={startX + sIdx * barW + 0.5}
                    y={scaleY(val)}
                    width={barW - 1}
                    height={h}
                    fill={COLORS[sIdx]}
                    rx={1.5}
                    opacity={0.8}
                  />
                );
              })}
            </g>
          );
        })}

        {/* X labels */}
        {MONTH_LABELS.map((label, i) => (
          <text key={i} x={scaleX(i)} y={svgH - 8} textAnchor="middle" fontSize={11} fill="#6b7280">
            {label}
          </text>
        ))}
      </svg>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-2 justify-center text-xs flex-wrap">
        {allSeries.map((s, i) => (
          <span key={s.label} className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded" style={{ backgroundColor: COLORS[i] }} />
            <span className="font-medium">{s.label}</span>
          </span>
        ))}
      </div>

      {/* Summary table */}
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="border-b text-muted-foreground">
              <th className="text-left py-1 pr-2 w-10">Mese</th>
              {allSeries.map((s, i) => (
                <th key={i} className="text-right py-1 px-1.5">
                  <span className="inline-flex items-center gap-1 justify-end">
                    <span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: COLORS[i] }} />
                    <span className="truncate">{s.label}</span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {MONTH_LABELS.map((label, mIdx) => (
              <tr key={label} className="border-b border-border/50">
                <td className="py-1 pr-2">{label}</td>
                {allSeries.map((s, sIdx) => {
                  const val = s.monthly[mIdx];
                  const pct = totals[sIdx] > 0 ? ((val / totals[sIdx]) * 100).toFixed(1) : "0";
                  return (
                    <td key={`${sIdx}-v`} className="text-right py-1 px-1.5 tabular-nums">
                      {formatNum(val)}
                      <span className="text-muted-foreground ml-0.5">({pct}%)</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
